import type { NavigateFunction } from "react-router-dom";
import type { AppDispatch } from "../redux/store";
import type {
  LoginResponseWithConsent,
  LoginResponseWithoutConsent,
  LoginResponsePasswordSetupRequired,
  FirstFactorAuthResponse,
} from "../Interfaces/LoginInterface";
import { userDataApi } from "../Apis/login";
import { setLoginMeta, clearLoginMeta } from "../redux/slices/LoginBetaSlice";
import { setUserContext } from "../redux/slices/UserSlice";
import { clearTokens, setTokens } from "../redux/slices/AuthSlice";
import {
  isPermissionGroupContextError,
  PERMISSION_GROUP_NOT_CONFIGURED_MESSAGE,
} from "../utils/teamLeaderAccess";
import { setPasswordSetup } from "../redux/slices/ResetPassword";
import { connectSocket } from "../services/socket";
import { BACKEND_URL } from "../Apis/auth";
import { toast } from "sonner";
import { getPostLoginPath } from "./completeAuthenticatedSession";
import { identifyUser, track } from "../analytics";

export function isEmailOtpLoginResponse(
  resp: unknown
): resp is LoginResponseWithoutConsent {
  return (
    !!resp &&
    typeof resp === "object" &&
    "emailOtpRequired" in resp &&
    (resp as LoginResponseWithoutConsent).emailOtpRequired === true
  );
}

export function isPhoneOtpLoginResponse(
  resp: unknown
): resp is LoginResponseWithoutConsent {
  return (
    !!resp &&
    typeof resp === "object" &&
    "phoneOtpRequired" in resp &&
    (resp as LoginResponseWithoutConsent).phoneOtpRequired === true
  );
}

export function isPasswordSetupRequiredResponse(
  resp: unknown
): resp is LoginResponsePasswordSetupRequired {
  return (
    !!resp &&
    typeof resp === "object" &&
    (resp as LoginResponsePasswordSetupRequired).passwordSetupRequired ===
      true &&
    typeof (resp as LoginResponsePasswordSetupRequired).passwordSetupToken ===
      "string"
  );
}

export interface PostAuthTokens {
  accessToken: string;
  refreshToken: string;
  accessExpiresInSec?: number;
  refreshExpiresAt?: string;
  jti?: string;
}

/**
 * Shared post-authentication flow: persist tokens, connect socket, load user context,
 * clear OTP meta, and navigate by role. Used by Login and 2FA completion paths.
 */
export async function completeAuthenticatedSession(
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  tokens: PostAuthTokens,
  options?: {
    clearLoginMeta?: boolean;
    successToast?: string;
    used2fa?: boolean;
  }
): Promise<void> {
  dispatch(
    setTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      accessExpiresInSec: tokens.accessExpiresInSec,
      refreshExpiresAt: tokens.refreshExpiresAt,
      jti: tokens.jti,
    })
  );

  connectSocket(tokens.accessToken, {
    serverUrl: BACKEND_URL || "http://localhost:5050",
  });

  if (options?.clearLoginMeta !== false) {
    dispatch(clearLoginMeta());
  }

  if (options?.successToast) {
    toast.success(options.successToast);
  }

  try {
    const userDataResponse = await userDataApi();
    dispatch(setUserContext(userDataResponse));
    // Analytics: identify + set tenant globals BEFORE the first event so it
    // carries tenant_id (US-A3), then the login_succeeded baseline (US-C1).
    // Fail-open (never throw), so they cannot break login.
    identifyUser(userDataResponse);
    track("login_succeeded", { used_2fa: options?.used2fa ?? false });
    navigate(getPostLoginPath(userDataResponse?.user?.roles));
  } catch (error) {
    console.error("error in userDataResponse", error);
    if (isPermissionGroupContextError(error)) {
      dispatch(clearTokens());
      toast.error(PERMISSION_GROUP_NOT_CONFIGURED_MESSAGE);
      return;
    }
    navigate("/my-leads");
  }
}

export async function handleLoginSuccess(
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  loginResponse:
    | LoginResponseWithConsent
    | LoginResponseWithoutConsent
    | LoginResponsePasswordSetupRequired
): Promise<void> {
  if (isPasswordSetupRequiredResponse(loginResponse)) {
    dispatch(
      setPasswordSetup({
        passwordSetupRequired: true,
        passwordSetupToken: loginResponse.passwordSetupToken,
      })
    );
    toast.info("Please set your password to continue.");
    navigate("/reset-password");
    return;
  }

  if (isEmailOtpLoginResponse(loginResponse)) {
    dispatch(
      setLoginMeta({
        emailOtpRequired: loginResponse.emailOtpRequired ?? false,
        loginToken: loginResponse.loginToken,
        devCode: loginResponse.devCode,
      })
    );
    toast.success("OTP Send to your email !");
    navigate("/two-factor-auth");
    return;
  }

  if (isPhoneOtpLoginResponse(loginResponse)) {
    dispatch(
      setLoginMeta({
        emailOtpRequired: loginResponse?.phoneOtpRequired ?? false,
        loginToken: loginResponse.loginToken,
        devCode: loginResponse.devCode,
      })
    );
    toast.success("OTP Send to your email !");
    navigate("/two-factor-auth");
    return;
  }

  const withConsent = loginResponse as LoginResponseWithConsent;
  await completeAuthenticatedSession(
    dispatch,
    navigate,
    {
      accessToken: withConsent.accessToken,
      refreshToken: withConsent.refreshToken,
      accessExpiresInSec: withConsent.accessExpiresInSec,
      refreshExpiresAt: withConsent.refreshExpiresAt,
      jti: withConsent.jti,
    },
    {
      clearLoginMeta: true,
      successToast: "Login Successfully ",
      used2fa: false,
    }
  );
}

export async function handleFirstFactorAuthSuccess(
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  response: FirstFactorAuthResponse
): Promise<void> {
  if (response.passwordSetupRequired) {
    dispatch(
      setPasswordSetup({
        passwordSetupRequired: true,
        passwordSetupToken: response.passwordSetupToken || "",
      })
    );
    dispatch(clearLoginMeta());
    navigate("/reset-password");
    return;
  }

  if (!response.accessToken || !response.refreshToken) {
    toast.error("Authentication failed. Please try again.");
    return;
  }

  await completeAuthenticatedSession(
    dispatch,
    navigate,
    {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      accessExpiresInSec: response.accessExpiresInSec,
      refreshExpiresAt: response.refreshExpiresAt,
      jti: response.jti,
    },
    { clearLoginMeta: true, successToast: "Login Successfully ", used2fa: true }
  );
}
